import { faker } from '@faker-js/faker';
import { lastDayOfMonth } from 'date-fns';
import { shuffle } from 'lodash-es';
import {
  type Compare,
  type DataPoint,
  type YearsMode,
  DefaultFilterValues,
} from '#src/types';
import {
  categoryOptions,
  categoryOptionsWithEarmarking,
} from './category-options';
import { geoValues } from './geo-values';
import { levelOptions } from './level-options';

type BuildMockDataProps = {
  isMonthly: boolean;
  yearsMode: YearsMode;
  selectedYears: number[];
  level: string;
  category: string;
  levelCompare: Compare;
  levelLimit?: number;
  categoryCompare?: Compare;
  categoryLimit?: number;
  fundingSourceCompare?: Compare;
};

export function buildMockData({
  isMonthly,
  yearsMode,
  selectedYears,
  level,
  category,
  levelCompare,
  levelLimit,
  categoryCompare,
  categoryLimit,
  fundingSourceCompare,
}: BuildMockDataProps): Array<Omit<DataPoint, 'date'> & { date: string }> {
  let sublevelIds: string[] = [];
  let subcategoryIds: string[] = [];
  let fundingSourceIds: string[] = [];

  let years = [...selectedYears];
  if (yearsMode === 'range') {
    const [startYear, endYear] = selectedYears;
    years = Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => startYear + index
    );
  }

  if (level !== DefaultFilterValues.level) {
    const comparedLevels = new Set(
      (levelCompare || []).flatMap((compare) => compare[0])
    );
    if (comparedLevels.size > 0) {
      sublevelIds = Array.from(comparedLevels);
    } else {
      const { options: levelData } = levelOptions[
        level as keyof typeof levelOptions
      ] as { options: { id: string }[] };
      let sortedLevelData = shuffle([...(levelData ?? [])]);

      sublevelIds = sortedLevelData
        .map((option) => option.id)
        .slice(0, levelLimit);
    }
  }

  if (category !== DefaultFilterValues.category) {
    const comparedCategories = new Set(
      (categoryCompare || []).flatMap((compare) => compare[0])
    );
    if (comparedCategories.size > 0) {
      subcategoryIds = Array.from(comparedCategories);
    } else {
      const _categoryOptions = isMonthly
        ? categoryOptionsWithEarmarking
        : categoryOptions;

      if (!(category in _categoryOptions)) {
        throw new Error(`Category ${category} not found`);
      }
      const { options: categoryData } =
        _categoryOptions[category as keyof typeof _categoryOptions];
      let sortedCategoryData = shuffle([...(categoryData ?? [])]);

      subcategoryIds = sortedCategoryData
        .map((option) => (option as { id: string }).id)
        .slice(0, categoryLimit);
    }
  }

  if (fundingSourceCompare && fundingSourceCompare.length > 0) {
    const comparedCategories = new Set(
      (fundingSourceCompare || []).flatMap((compare) => compare[0])
    );
    fundingSourceIds = Array.from(new Set(comparedCategories));
  }

  // Generate all possible combinations of years, sublevelIds, and subcategoryIds
  const data: Array<Omit<DataPoint, 'date'> & { date: string }> = [];

  // Use null as default when arrays are empty
  const finalSublevelIds = sublevelIds.length > 0 ? sublevelIds : [null];
  const finalSubcategoryIds =
    subcategoryIds.length > 0 ? subcategoryIds : [null];
  const finalFundingSourceIds =
    fundingSourceIds.length > 0 ? fundingSourceIds : [null];

  for (const year of years) {
    let months: (number | null)[] = [null];
    if (isMonthly) {
      months = Array.from({ length: 12 }, (_, index) => index);
    }
    for (const sublevelId of finalSublevelIds) {
      const geoValuesForLevel = geoValues[level as keyof typeof geoValues];
      const geoIds = Object.values(geoValuesForLevel);
      const randomGeoId =
        geoIds[faker.number.int({ min: 0, max: geoIds.length - 1 })];

      for (const subcategoryId of finalSubcategoryIds) {
        for (const fundingSourceId of finalFundingSourceIds) {
          let value = faker.number.int({ min: 0, max: 10_000_000 });
          let date = new Date(year, 0, 1).toISOString();
          for (const month of months) {
            if (month != null && month > 0) {
              value = Math.round(value * 1.1);
            }

            if (month != null) {
              date = lastDayOfMonth(new Date(year, month, 1)).toISOString();
            }

            data.push({
              value,
              date,
              level,
              category,
              sublevelId,
              subcategoryId,
              fundingSourceId,
              geoId: randomGeoId,
              percentGrowthLastYear: faker.number.float({ min: 0, max: 100 }),
            });
          }
        }
      }
    }
  }

  return data;
}
